import { useCallback, useEffect, useRef, useState } from "react";
import { abortableDelay } from "../../../api/abortable";
import { inspectArtworkFailure } from "../api/nft-artwork";

/** Bounded recovery for one mounted canonical URL, independent of NFT write permissions. */
export function useArtworkLoad(imageUrl: string, onMetadataMismatch?: () => void) {
  const [state, setState] = useState({ attempt: 0, failed: false, retrying: false, changed: false });
  const lifetime = useRef<AbortController | null>(null);
  const pending = useRef(false);
  const attempts = useRef(0);
  const refresh = useRef(onMetadataMismatch);
  useEffect(() => { refresh.current = onMetadataMismatch; }, [onMetadataMismatch]);
  useEffect(() => {
    const abort = new AbortController();
    lifetime.current = abort;
    return () => { abort.abort(); if (lifetime.current === abort) lifetime.current = null; };
  }, [imageUrl]);

  const onError = useCallback(() => {
    const abort = lifetime.current;
    if (!abort || abort.signal.aborted || pending.current) return;
    setState(s => ({ ...s, failed: true, retrying: false }));
    if (!imageUrl.startsWith("https:") || attempts.current >= 3) return;
    pending.current = true;
    const recover = async () => {
      // Do not probe immediately after an image failure during server overload.
      const backoff = 2_000 * 2 ** attempts.current;
      attempts.current++;
      await abortableDelay(backoff, abort.signal);
      const failure = await inspectArtworkFailure(imageUrl, abort.signal);
      abort.signal.throwIfAborted();
      if (failure.refreshMetadata) {
        setState(s => ({ ...s, changed: true, retrying: false }));
        refresh.current?.();
        return;
      }
      // Very long server cooldowns stop automatic recovery instead of retrying early.
      if (!failure.retry || (failure.retryAfterMs !== null && failure.retryAfterMs > 60_000)) {
        setState(s => ({ ...s, retrying: false }));
        return;
      }
      await abortableDelay(failure.retryAfterMs ?? 0, abort.signal);
      abort.signal.throwIfAborted();
      setState(s => ({ ...s, attempt: s.attempt + 1, failed: false, retrying: false }));
    };
    setState(s => ({ ...s, retrying: true }));
    recover().catch(() => {
      if (!abort.signal.aborted) setState(s => ({ ...s, retrying: false }));
    }).finally(() => { pending.current = false; }).catch(() => {});
  }, [imageUrl]);
  return { ...state, onError };
}
